import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import NotificationPanel from '../NotificationPanel';
import toast from 'react-hot-toast';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + key shortcuts
      if (e?.altKey && !e?.ctrlKey && !e?.shiftKey && !e?.metaKey) {
        switch (e?.key?.toLowerCase()) {
          case 'd':
            e?.preventDefault();
            navigate('/dashboard');
            toast?.success('Navigated to Dashboard');
            break;
          case 't':
            e?.preventDefault();
            navigate('/transaction-management');
            toast?.success('Navigated to Transactions');
            break;
          case 'r':
            e?.preventDefault();
            navigate('/reports-analytics');
            toast?.success('Navigated to Reports');
            break;
          case 'i':
            e?.preventDefault();
            navigate('/investment-tracker');
            toast?.success('Navigated to Investments');
            break;
          case 's':
            e?.preventDefault();
            navigate('/systems-management');
            toast?.success('Navigated to Systems');
            break;
          case 'g':
            e?.preventDefault();
            navigate('/settings');
            toast?.success('Navigated to Settings');
            break;
          case 'b':
            e?.preventDefault();
            navigate('/recurring-bill-tracker');
            toast?.success('Navigated to Bills');
            break;
          default:
            break;
        }
      }
      // Escape to close modals
      if (e?.key === 'Escape') {
        if (showNotifications) setShowNotifications(false);
        if (showUserMenu) setShowUserMenu(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showNotifications, showUserMenu]);

  // Reorganized navigation with logical grouping
  const navigationSections = [
    {
      id: 'overview',
      label: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', shortcut: 'Alt+D' },
        { path: '/reports-analytics', label: 'Reports', icon: 'BarChart3', shortcut: 'Alt+R' }
      ]
    },
    {
      id: 'finances',
      label: 'Finances',
      items: [
        { path: '/transaction-management', label: 'Transactions', icon: 'Receipt', shortcut: 'Alt+T' },
        { path: '/recurring-bill-tracker', label: 'Bills', icon: 'FileText', shortcut: 'Alt+B' },
        { path: '/receipt-scanner', label: 'Receipt Scanner', icon: 'Camera', shortcut: 'Alt+C' }
      ]
    },
    {
      id: 'planning',
      label: 'Planning',
      items: [
        { path: '/investment-tracker', label: 'Investments', icon: 'TrendingUp', shortcut: 'Alt+I' },
        { path: '/retirement-calculator', label: 'Retirement', icon: 'PiggyBank' },
        { path: '/debt-payoff-calculator', label: 'Debt Payoff', icon: 'Calculator' }
      ]
    },
    {
      id: 'growth',
      label: 'Growth',
      items: [
        { path: '/systems-management', label: 'Systems', icon: 'Target', shortcut: 'Alt+S' }
      ]
    }
  ];

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast?.success('Signed out successfully');
      navigate('/signin');
    } catch (error) {
      toast?.error('Failed to sign out');
    }
  };

  return (
    <header 
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-200' 
          : 'bg-white border-b border-gray-100'
      }`}
      role="banner"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 group transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded-lg px-2 py-1"
            aria-label="My Financial App Home"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-200">
              <Icon name="Leaf" size={24} color="#FFFFFF" strokeWidth={2.5} aria-hidden="true" />
            </div>
            <span className="hidden lg:block text-lg font-bold text-gray-900 group-hover:text-green-700 transition-colors duration-200">
              My Financial App
            </span>
            <span className="lg:hidden text-lg font-bold text-gray-900 group-hover:text-green-700 transition-colors duration-200">
              My Financial App
            </span>
          </Link>

          {/* Desktop Navigation with Sections */}
          <nav className="hidden xl:flex items-center gap-6" role="navigation" aria-label="Primary navigation">
            {navigationSections?.map((section) => (
              <div key={section?.id} className="flex items-center gap-1">
                {section?.items?.map((item, index) => {
                  const active = isActive(item?.path);
                  return (
                    <React.Fragment key={item?.path}>
                      {index === 0 && section?.id !== 'overview' && (
                        <div className="w-px h-6 bg-gray-200 mx-2" aria-hidden="true" />
                      )}
                      <Link
                        to={item?.path}
                        className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                          active
                            ? 'text-green-700 bg-green-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                        aria-current={active ? 'page' : undefined}
                        aria-label={`${item?.label}${item?.shortcut ? ` (${item?.shortcut})` : ''}`}
                        title={`${section?.label}: ${item?.label}${item?.shortcut ? ` (${item?.shortcut})` : ''}`}
                      >
                        <Icon 
                          name={item?.icon} 
                          size={18} 
                          color="currentColor" 
                          strokeWidth={active ? 2.5 : 2}
                          aria-hidden="true"
                        />
                        <span>{item?.label}</span>
                        {active && (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-green-600 rounded-full" aria-hidden="true" />
                        )}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </div>
            ))}
            
            {/* Settings link */}
            <div className="w-px h-6 bg-gray-200 mx-2" aria-hidden="true" />
            <Link
              to="/settings"
              className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                isActive('/settings')
                  ? 'text-green-700 bg-green-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              aria-current={isActive('/settings') ? 'page' : undefined}
              aria-label="Settings (Alt+G)"
              title="Settings (Alt+G)"
            >
              <Icon 
                name="Settings" 
                size={18} 
                color="currentColor" 
                strokeWidth={isActive('/settings') ? 2.5 : 2}
                aria-hidden="true"
              />
              <span>Settings</span>
              {isActive('/settings') && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-green-600 rounded-full" aria-hidden="true" />
              )}
            </Link>
          </nav>

          {/* User Actions */}
          {user && (
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
                aria-label="Toggle notifications"
                aria-expanded={showNotifications}
                aria-describedby="notification-badge"
              >
                <Icon name="Bell" size={20} color="#374151" strokeWidth={2} aria-hidden="true" />
                {/* Notification badge - can be made dynamic */}
                <span 
                  id="notification-badge"
                  className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" 
                  aria-label="You have unread notifications"
                />
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
                  aria-label="User menu"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center ring-2 ring-green-600/20">
                    <Icon name="User" size={18} color="#166534" strokeWidth={2.5} aria-hidden="true" />
                  </div>
                </button>

                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                      aria-hidden="true"
                    />
                    <div 
                      className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                      role="menu"
                      aria-label="User menu"
                    >
                      <div className="p-4 border-b border-gray-100 bg-gray-50">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {user?.email}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">Account Settings</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/settings"
                          className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
                          onClick={() => setShowUserMenu(false)}
                          role="menuitem"
                          aria-label="Settings (Alt+G)"
                        >
                          <Icon name="Settings" size={18} color="currentColor" strokeWidth={2} aria-hidden="true" />
                          Settings
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                          role="menuitem"
                          aria-label="Sign out"
                        >
                          <Icon name="LogOut" size={18} color="currentColor" strokeWidth={2} aria-hidden="true" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </header>
  );
};

export default Header;